import React, { useState, useContext, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { DirectionContext } from "../App";
import Cookies from "js-cookie";

const ApplicationTypePage = () => {
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const { navigateTo } = useContext(DirectionContext);
  const [selectedOption, setSelectedOption] = useState("");

  // 페이지 로드 시 스크롤 위치를 맨 위로 초기화
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 다음 단계로 이동
  const handleNext = () => {
    if (selectedOption) {
      // 선택한 신청 방식을 쿠키에 저장
      Cookies.set("applicationType", selectedOption, {
        expires: 7,
        path: "/",
      });

      // 다음 페이지로 이동
      navigateTo("/user-data");
    }
  };

  // 버튼 활성화 여부
  const isNextButtonEnabled = selectedOption !== "";

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <div>
            <Title>2. 신청 방식 선택하기</Title>
          </div>
          <Header>
            <StepIndicator>2 / 4</StepIndicator>
            <SubTitle>신청 방식 선택하기</SubTitle>
            <Description>집단소송 참여 방법을 선택해주세요</Description>
          </Header>

          {/* <FormContainer> */}
          <RadioGroup>
            <RadioOption
              isSelected={selectedOption === "self"}
              onClick={() => setSelectedOption("self")}
            >
              <RadioLabel>
                <RadioLabelTitle>본인 신청</RadioLabelTitle>
                <RadioLabelText>
                  신청자 본인의 명의만 가입합니다.
                </RadioLabelText>
              </RadioLabel>
              <RadioCircle isSelected={selectedOption === "self"}>
                {selectedOption === "self" && <RadioInner />}
              </RadioCircle>
            </RadioOption>
            <RadioOption
              isSelected={selectedOption === "representative"}
              onClick={() => setSelectedOption("representative")}
            >
              <RadioLabel>
                <RadioLabelTitle>대표자 신청</RadioLabelTitle>
                <RadioLabelText>
                  가족/단체의 대표자로 가입합니다.
                </RadioLabelText>
              </RadioLabel>
              <RadioCircle isSelected={selectedOption === "representative"}>
                {selectedOption === "representative" && <RadioInner />}
              </RadioCircle>
            </RadioOption>
          </RadioGroup>
          {/* </FormContainer> */}

          <ButtonsContainer>
            <ActionButton
              onClick={handleNext}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={!isNextButtonEnabled}
            >
              다음으로
            </ActionButton>
          </ButtonsContainer>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f8f8f8;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 30px 20px 40px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 20px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StepIndicator = styled.div`
  display: flex;
  width: 50px;
  height: 29px;
  padding: 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #0075ff;
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 15px;
  margin-top: 5px;
  align-self: flex-start;
`;

const Title = styled.h1`
  color: #00285b;
  text-align: center;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.34px;
  margin: 5px 0;
  padding-top: 10px;
  word-break: keep-all;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 10px;
  line-height: 1.4;
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
  border-radius: 12px;
  border: 1px solid rgba(0, 117, 255, 0.3);
  overflow: hidden;
  background-color: white;
  padding: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 170px;
  width: 100%;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-radius: 8px;
  background-color: ${(props) =>
    props.isSelected ? "rgba(0, 117, 255, 0.1)" : "white"};
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${(props) => (props.isSelected ? "#0075FF" : "#e5e5e5")};
  width: 100%;

  &:hover {
    background-color: ${(props) =>
      props.isSelected ? "rgba(0, 117, 255, 0.1)" : "rgba(0, 117, 255, 0.05)"};
  }
`;

const RadioCircle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid ${(props) => (props.isSelected ? "#0075FF" : "#ccc")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const RadioInner = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #0075ff;
`;

const RadioLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RadioLabelTitle = styled.div`
  color: #2e3840;
  font-size: 16px;
  font-weight: 700;
  line-height: normal;
`;

const RadioLabelText = styled.div`
  color: #4b5563;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
`;

const ButtonsContainer = styled.div`
  max-width: 400px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-top: 0px;
  padding: 0 0px;
  background-color: #f8f8f8;
  margin: 0 auto;

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
    justify-content: center;
  }
`;

const ActionButton = styled(motion.button)`
  flex: 1;
  display: flex;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: #0075ff;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
`;

export default ApplicationTypePage;
